import React, { useState, useEffect } from 'react';
import Joyride, { CallBackProps, Step, STATUS, ACTIONS } from 'react-joyride'; // Add ACTIONS import

interface TourGuideProps {
  steps: Step[];
  tourId: string;
  run?: boolean;
  continuous?: boolean;
  showSkipButton?: boolean;
  showProgress?: boolean;
  onComplete?: () => void;
  onSkip?: () => void;
  onFinish?: () => void;
}

const TourGuide: React.FC<TourGuideProps> = ({
  steps,
  tourId,
  run = false,
  continuous = true,
  showSkipButton = true,
  showProgress = true,
  onComplete,
  onSkip,
  onFinish
}) => {
  const [isRunning, setIsRunning] = useState(run);
  const [stepIndex, setStepIndex] = useState(0);

  // Always respect the run prop, regardless of localStorage status
  useEffect(() => {
    if (run) {
      // If tour is explicitly started, reset to first step
      setStepIndex(0);
      setIsRunning(true);
    } else {
      setIsRunning(false);
    }
  }, [run]);

  const handleCallback = (data: CallBackProps) => {
    const { action, index, status, type } = data;

    // Handle X button clicks and all other ways to close the tour
    if (action === ACTIONS.CLOSE || action === ACTIONS.SKIP) {
      setIsRunning(false);
      if (onSkip) onSkip();
      return;
    }

    // Handle navigation between steps
    if (type === 'step:after') {
      if (action === ACTIONS.NEXT) {
        setStepIndex(index + 1);
        localStorage.setItem(`tour_${tourId}_step`, String(index + 1));
      } else if (action === ACTIONS.PREV) {
        setStepIndex(index - 1);
        localStorage.setItem(`tour_${tourId}_step`, String(index - 1));
      }
    }

    // Handle tour completion
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      setIsRunning(false);
      
      if (status === STATUS.FINISHED) {
        localStorage.setItem(`tour_${tourId}_completed`, 'true');
        if (onComplete) onComplete();
        if (onFinish) onFinish();
      }
      
      if (status === STATUS.SKIPPED) {
        if (onSkip) onSkip();
      }
    }
  };

  return (
    <Joyride
      callback={handleCallback}
      continuous={continuous}
      run={isRunning}
      scrollToFirstStep={true}
      showProgress={showProgress}
      showSkipButton={showSkipButton}
      stepIndex={stepIndex}
      steps={steps}
      disableCloseOnEsc={false} // Ensure ESC key can close the tour
      disableOverlayClose={false} // Allow clicking the overlay to close the tour
      styles={{
        options: {
          primaryColor: '#3498db',
          backgroundColor: '#ffffff',
          textColor: '#333333',
          zIndex: 10000, // Ensure tour appears above other elements
        },
        buttonClose: {
          display: 'block', // Ensure close button is visible
          cursor: 'pointer',
        },
        tooltipContainer: {
          textAlign: 'center',
        },
        buttonNext: {
          backgroundColor: '#3498db',
          fontSize: 14,
        },
        buttonBack: {
          marginRight: 10,
          fontSize: 14,
        }
      }}
      locale={{
        close: "Close", // Ensure close button has text
        last: "Finish",
        next: "Next",
        skip: "Skip",
        back: "Back"
      }}
    />
  );
};

export default TourGuide;